import { ChangeArrowDown, ChangeArrowUp } from "common/Icons/Icons";
import styled, { css, useTheme } from "styled-components";
import { RootState } from "app/store";
import { PrecisionConfig } from "types";
import React, { CSSProperties, ReactNode } from "react";
import { hexToRGB } from "@shared/utils/misc";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon";
import { PrecisionType } from "@shared/types";

interface ChangeTagProps {
  value: number;
  suffix?: string;
  prefix?: ReactNode;
  precisionSelector?: (state: RootState) => PrecisionConfig;
  precisionType?: PrecisionType;
  precisionCoin?: string;
  precisionConfig?: PrecisionConfig;
  precision?: number;
  exchange?: string;
  showArrow?: boolean;
  style?: CSSProperties;
  colorZeroValue?: boolean;
  arrowStyle?: CSSProperties;
  hideBackground?: boolean;
  absoluteValue?: boolean;
  noOpacityColor?: boolean;
  textStyle?: CSSProperties;
}

const ChangeTag = ({
  value,
  suffix,
  prefix,
  precisionSelector,
  precisionType,
  precisionCoin,
  precisionConfig,
  precision,
  exchange,
  showArrow = true,
  style,
  colorZeroValue,
  arrowStyle,
  hideBackground,
  absoluteValue,
  noOpacityColor,
  textStyle,
}: ChangeTagProps) => {
  const { isNewLook } = useTheme();
  if (value === null || value === undefined) return null;
  const isNegative = value < 0;
  // The new look keeps a zero change neutral unless asked otherwise.
  const isPositive =
    value > 0 || (value === 0 && (colorZeroValue ?? !isNewLook));

  let displayValue: React.JSX.Element | number = value;
  if (absoluteValue) displayValue = Math.abs(displayValue);
  // The new look writes the sign ("+2.7%", "−0.6%") instead of an arrow.
  const newLookSign =
    isNewLook && value !== 0 ? (isNegative ? "\u2212" : "+") : "";
  if (isNewLook) displayValue = Math.abs(value);

  if (((precisionSelector || precisionConfig) && precisionType) || precision) {
    displayValue = (
      <TextWithPrecisionAndIcon
        text={displayValue}
        precisionSelector={precisionSelector}
        precisionType={precisionType}
        precisionCoin={precisionCoin}
        precisionConfig={precisionConfig}
        precision={precision}
        exchange={exchange}
        round={true}
      />
    );
  }

  return (
    <ChangeWrapper style={style}>
      <ChangeValue
        $isNegative={isNegative}
        $isPositive={isPositive}
        $hideBackground={hideBackground}
        $noOpacityColor={noOpacityColor}
        style={textStyle}
      >
        {newLookSign}
        {prefix}
        {displayValue}
        {suffix}
        {showArrow && !isNewLook && (
          <ArrowWrapper style={arrowStyle}>
            {isNegative && <ChangeArrowDown />}
            {isPositive && <ChangeArrowUp />}
          </ArrowWrapper>
        )}
      </ChangeValue>
    </ChangeWrapper>
  );
};

export default ChangeTag;

const ChangeWrapper = styled.div`
  font-size: 14px;
  display: flex;
  align-items: center;
  margin-left: 8px;
`;
const ChangeValue = styled.div<{
  $isNegative: boolean;
  $isPositive: boolean;
  $hideBackground?: boolean;
  $noOpacityColor?: boolean;
}>`
  display: flex;
  align-items: center;
  ${(p) =>
    (p.$isPositive || p.$isNegative) &&
    !p.$hideBackground &&
    `
    padding: 0 8px;
    font-weight: 500;
  `};
  border-radius: 4px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      border-radius: 6px;
      font-size: 12.5px;
      font-variant-numeric: tabular-nums;
      white-space: nowrap;
      ${(p.$isPositive || p.$isNegative) &&
      !p.$hideBackground &&
      css`
        padding: 1px 6px;
        font-weight: ${p.theme.typography.fontWeights.MEDIUM};
      `}
    `}
  ${(p) =>
    p.$isNegative &&
    ` 
    color: ${p.$noOpacityColor ? p.theme.colors.white : p.theme.colors.red};
    ${
      !p.$hideBackground &&
      `background: ${hexToRGB(
        p.theme.colors.red,
        p.$noOpacityColor ? 1 : 0.1
      )};`
    };
  `}
  ${(p) =>
    p.$isPositive &&
    `
    color: ${p.$noOpacityColor ? p.theme.colors.white : p.theme.colors.green};
     ${
       !p.$hideBackground &&
       `background: ${hexToRGB(
         p.theme.colors.green,
         p.$noOpacityColor ? 1 : 0.1
       )};`
     };
  `}
`;
const ArrowWrapper = styled.div`
  margin-left: 5px;
  font-size: 10px;
  display: flex;
  align-items: center;
`;
